import React, { useEffect, useState } from "react";
import { api, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StickyNote, Pin, PinOff, Trash2, Pencil, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function Notes() {
  const [notes, setNotes] = useState(null);
  const [content, setContent] = useState("");
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editDate, setEditDate] = useState(todayISO());

  const load = async () => {
    const { data } = await api.get("/notes");
    setNotes(data);
  };
  useEffect(() => { load(); }, []);

  const addNote = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error("Écris quelque chose avant d'enregistrer");
      return;
    }
    setSaving(true);
    try {
      await api.post("/notes", { content, date, pinned: false });
      setContent("");
      setDate(todayISO());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      addNote(e);
    }
  };

  const togglePin = async (n) => {
    await api.put(`/notes/${n.id}`, { content: n.content, date: n.date, pinned: !n.pinned });
    load();
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setEditContent(n.content);
    setEditDate(n.date);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent("");
  };

  const saveEdit = async (n) => {
    if (!editContent.trim()) {
      toast.error("La note ne peut pas être vide");
      return;
    }
    await api.put(`/notes/${n.id}`, { content: editContent, date: editDate, pinned: n.pinned });
    setEditingId(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    await api.delete(`/notes/${id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Pense-bête</div>
        <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Notes libres</h1>
      </div>

      {/* Ajout rapide */}
      <div className="card-surface p-5">
        <form onSubmit={addNote} className="space-y-3">
          <Textarea
            rows={3}
            placeholder="Un rappel, une idée, un truc à ne pas oublier…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            data-testid="input-note-content"
          />
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full sm:w-44"
              data-testid="input-note-date"
            />
            <Button type="submit" disabled={saving} className="sm:self-end" data-testid="btn-add-note">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <StickyNote className="h-4 w-4 mr-2" />}
              Ajouter la note
            </Button>
          </div>
        </form>
      </div>

      {/* Liste */}
      {notes === null ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : notes.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-muted-foreground" data-testid="notes-empty">
          Aucune note pour l'instant. Écris la première ci-dessus.
        </div>
      ) : (
        <div className="space-y-3" data-testid="notes-list">
          {notes.map((n) => (
            <div
              key={n.id}
              className={`card-surface p-4 ${n.pinned ? "border-primary/40" : ""}`}
              data-testid={`note-${n.id}`}
            >
              {editingId === n.id ? (
                <div className="space-y-3">
                  <Textarea rows={3} value={editContent} onChange={(e) => setEditContent(e.target.value)} data-testid={`input-edit-note-${n.id}`} />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                    <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className="w-full sm:w-44" />
                    <div className="flex gap-2 sm:self-end">
                      <Button type="button" variant="ghost" size="sm" onClick={cancelEdit}>
                        <X className="h-4 w-4 mr-1" /> Annuler
                      </Button>
                      <Button type="button" size="sm" onClick={() => saveEdit(n)} data-testid={`btn-save-edit-note-${n.id}`}>
                        <Check className="h-4 w-4 mr-1" /> Enregistrer
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground capitalize">
                      {n.pinned && <Pin className="h-3.5 w-3.5 text-primary" />}
                      {formatDate(n.date)}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => togglePin(n)}
                        className="text-muted-foreground hover:text-primary p-1.5 rounded-md hover:bg-muted transition-colors"
                        title={n.pinned ? "Désépingler" : "Épingler"}
                        data-testid={`btn-pin-note-${n.id}`}
                      >
                        {n.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => startEdit(n)}
                        className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-muted transition-colors"
                        title="Modifier"
                        data-testid={`btn-edit-note-${n.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(n.id)}
                        className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-destructive/10 transition-colors"
                        title="Supprimer"
                        data-testid={`btn-delete-note-${n.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{n.content}</div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
